import React, { useState, useEffect } from 'react';
import { Users, Mail, Calendar, RefreshCw, ArrowLeft, Download, Eye, Globe, MapPin, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { setRobotsMeta } from '../lib/seo';

const FounderDashboard = () => {
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({ total: 0, emailsSent: 0, today: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [activeTab, setActiveTab] = useState('signups');
  const [adminKey, setAdminKey] = useState('');
  const [keyDraft, setKeyDraft] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    setRobotsMeta('noindex, nofollow');
    return () => setRobotsMeta('index, follow');
  }, []);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isAllowed = 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' ||
      hostname.startsWith('app.') ||
      hostname.includes('preview') ||
      hostname === 'archen.io' ||
      hostname === 'www.archen.io';
    
    if (!isAllowed) {
      navigate('/');
      return;
    }
    setAuthorized(true);
  }, [navigate]);

  const fetchSignups = async () => {
    try {
      const response = await fetch(`/api/waitlist/signups`, { headers: { 'X-Admin-Key': adminKey } });
      if (response.status === 401) {
        setAdminKey('');
        setAuthError('Session expired. Enter the admin key again.');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSignups(data.signups || []);
        
        const today = new Date().toISOString().split('T')[0];
        const todaySignups = data.signups.filter(s => s.created_at.startsWith(today)).length;
        const emailsSent = data.signups.filter(s => s.email_sent).length;
        
        setStats({
          total: data.signups.length,
          emailsSent,
          today: todaySignups
        });
      }
    } catch (error) {
      console.error('Error fetching signups:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`/api/analytics/stats`, { headers: { 'X-Admin-Key': adminKey } });
      if (response.status === 401) {
        setAdminKey('');
        setAuthError('Session expired. Enter the admin key again.');
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  useEffect(() => {
    if (authorized && adminKey) {
      fetchSignups();
      fetchAnalytics();
    }
  }, [authorized, adminKey]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const response = await fetch('/api/analytics/stats', { headers: { 'X-Admin-Key': keyDraft } });
      if (response.status === 401) {
        setAuthError('Incorrect admin key.');
        return;
      }
      if (!response.ok) {
        setAuthError('Something went wrong. Please try again.');
        return;
      }
      setAdminKey(keyDraft);
      setKeyDraft('');
    } catch (error) {
      setAuthError('Could not reach the server.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSignups();
    fetchAnalytics();
  };

  const handleDelete = async (signup) => {
    const ok = window.confirm(`Delete waitlist entry for ${signup.email}? This can't be undone.`);
    if (!ok) return;
    try {
      const response = await fetch(`/api/waitlist/signups/${signup.id}`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
      if (response.status === 401) {
        setAdminKey('');
        setAuthError('Session expired. Enter the admin key again.');
        return;
      }
      if (response.ok) {
        setSignups(prev => prev.filter(s => s.id !== signup.id));
        setStats(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
      } else {
        const data = await response.json().catch(() => ({}));
        alert(`Failed to delete: ${data.detail || response.statusText}`);
      }
    } catch (e) {
      alert(`Failed to delete: ${e.message}`);
    }
  };


  const handleExportCSV = () => {
    const headers = ['Email', 'First Name', 'Last Name', 'Company', 'Signed Up', 'Email Sent'];
    const rows = signups.map(s => [
      s.email,
      s.first_name || '',
      s.last_name || '',
      s.company || '',
      new Date(s.created_at).toLocaleString(),
      s.email_sent ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `archen-waitlist-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!authorized) {
    return null;
  }

  if (!adminKey) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center px-6">
        <form
          onSubmit={handleUnlock}
          data-testid="admin-key-form"
          className="bg-white rounded-xl border border-[#E5DDD0] shadow-sm p-8 w-full max-w-sm"
        >
          <h1 className="text-[#3D3229] text-lg font-semibold mb-1">Founder Dashboard</h1>
          <p className="text-[#8B7355] text-sm mb-5">Enter the admin key to continue.</p>
          <input
            type="password"
            autoComplete="off"
            value={keyDraft}
            onChange={(e) => setKeyDraft(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-key-input"
            className="w-full rounded-lg border border-[#E5DDD0] px-3 py-2.5 text-sm text-[#3D3229] focus:outline-none focus:ring-2 focus:ring-[#B8864B]/40 mb-3"
          />
          {authError && (
            <p className="text-[#C87850] text-sm mb-3" data-testid="admin-key-error">{authError}</p>
          )}
          <Button
            type="submit"
            disabled={authLoading || !keyDraft}
            data-testid="admin-key-submit"
            className="w-full bg-gradient-to-r from-[#B8864B] to-[#A07040] text-white hover:from-[#A07040] hover:to-[#8B6030]"
          >
            {authLoading ? 'Checking...' : 'Unlock'}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-[#E5DDD0] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="text-[#5C4F42] hover:text-[#3D3229] transition-colors">
              <ArrowLeft size={20} />
            </a>
            <div>
              <h1 className="text-[#3D3229] text-xl font-semibold">Founder Dashboard</h1>
              <p className="text-[#8B7355] text-sm">Waitlist & Analytics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#E5DDD0] text-[#5C4F42] hover:bg-[#F5EBE0]"
              disabled={signups.length === 0}
            >
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
            <Button
              onClick={handleRefresh}
              disabled={refreshing}
              className="bg-gradient-to-r from-[#B8864B] to-[#A07040] text-white hover:from-[#A07040] hover:to-[#8B6030]"
            >
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-[#E5DDD0] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#B8864B]/10 to-[#A07040]/10 flex items-center justify-center">
                <Users size={20} className="text-[#B8864B]" />
              </div>
              <div>
                <p className="text-[#8B7355] text-xs">Waitlist</p>
                <p className="text-[#3D3229] text-2xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 border border-[#E5DDD0] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#5B7B6A]/10 to-[#4A6B5A]/10 flex items-center justify-center">
                <Mail size={20} className="text-[#5B7B6A]" />
              </div>
              <div>
                <p className="text-[#8B7355] text-xs">Emails Sent</p>
                <p className="text-[#3D3229] text-2xl font-semibold">{stats.emailsSent}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-5 border border-[#E5DDD0] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#4A6B8A]/10 to-[#3A5B7A]/10 flex items-center justify-center">
                <Eye size={20} className="text-[#4A6B8A]" />
              </div>
              <div>
                <p className="text-[#8B7355] text-xs">Total Visits</p>
                <p className="text-[#3D3229] text-2xl font-semibold">{analytics?.total_visits || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5 border border-[#E5DDD0] shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#8B6A9B]/10 to-[#7A5A8A]/10 flex items-center justify-center">
                <Calendar size={20} className="text-[#8B6A9B]" />
              </div>
              <div>
                <p className="text-[#8B7355] text-xs">Today Visits</p>
                <p className="text-[#3D3229] text-2xl font-semibold">{analytics?.today_visits || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('signups')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'signups' 
                ? 'bg-[#3D3229] text-white' 
                : 'bg-white text-[#5C4F42] border border-[#E5DDD0] hover:bg-[#F5EBE0]'
            }`}
          >
            Waitlist
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'analytics' 
                ? 'bg-[#3D3229] text-white' 
                : 'bg-white text-[#5C4F42] border border-[#E5DDD0] hover:bg-[#F5EBE0]'
            }`}
          >
            Site Analytics
          </button>
        </div>

        {activeTab === 'signups' ? (
          /* Signups Table */
          <div className="bg-white rounded-xl border border-[#E5DDD0] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E5DDD0]">
              <h2 className="text-[#3D3229] text-lg font-semibold">Waitlist</h2>
              <p className="text-[#8B7355] text-sm">People who joined the Archen™ waitlist</p>
            </div>
            
            {loading ? (
              <div className="p-12 text-center">
                <RefreshCw size={32} className="mx-auto text-[#B8864B] animate-spin mb-4" />
                <p className="text-[#8B7355]">Loading requests...</p>
              </div>
            ) : signups.length === 0 ? (
              <div className="p-12 text-center">
                <Users size={48} className="mx-auto text-[#D4C8B8] mb-4" />
                <p className="text-[#5C4F42] font-medium mb-2">No one on the waitlist yet</p>
                <p className="text-[#8B7355] text-sm">Share your landing page to start growing the waitlist!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F4EE]">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#8B7355] uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#8B7355] uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#8B7355] uppercase tracking-wider">Company</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#8B7355] uppercase tracking-wider">Signed Up</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[#8B7355] uppercase tracking-wider">Email Sent</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[#8B7355] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5DDD0]">
                    {signups.map((signup) => (
                      <tr key={signup.id} className="hover:bg-[#FDF8F3]/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B8864B] to-[#A07040] flex items-center justify-center text-white text-sm font-medium">
                              {(signup.first_name || signup.email)[0].toUpperCase()}
                            </div>
                            <span className="text-[#3D3229] font-medium">
                              {signup.first_name && signup.last_name 
                                ? `${signup.first_name} ${signup.last_name}` 
                                : signup.name || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[#5C4F42]">{signup.email}</td>
                        <td className="px-6 py-4 text-[#5C4F42]">{signup.company || <span className="text-[#A69580]">-</span>}</td>
                        <td className="px-6 py-4 text-[#5C4F42] text-sm">{formatDate(signup.created_at)}</td>
                        <td className="px-6 py-4">
                          {signup.email_sent ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#5B7B6A]/10 text-[#5B7B6A]">✓ Sent</span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#C87850]/10 text-[#C87850]">Pending</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            data-testid={`delete-signup-${signup.id}`}
                            onClick={() => handleDelete(signup)}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#A69580] hover:text-[#C87850] hover:bg-[#C87850]/10 transition-colors"
                            title="Delete entry"
                          >
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          /* Analytics Tab */
          <div className="space-y-6">
            {/* Daily Visits Chart */}
            <div className="bg-white rounded-xl border border-[#E5DDD0] shadow-sm p-6">
              <h3 className="text-[#3D3229] font-semibold mb-4">Visits (Last 7 Days)</h3>
              <div className="flex items-end gap-2 h-32">
                {analytics?.daily_visits?.map((day, i) => {
                  const maxCount = Math.max(...(analytics?.daily_visits?.map(d => d.count) || [1]), 1);
                  const height = (day.count / maxCount) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs text-[#8B7355]">{day.count}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-[#B8864B] to-[#D4A574] rounded-t transition-all"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <span className="text-xs text-[#8B7355]">{day.date}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Visitor Map */}
              <div className="bg-white rounded-xl border border-[#E5DDD0] shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Globe size={18} className="text-[#5C4F42]" />
                  <h3 className="text-[#3D3229] font-semibold">Visitor Locations</h3>
                </div>
                <div className="relative bg-[#F5EBE0] rounded-lg overflow-hidden" style={{ height: '200px' }}>
                  {/* Simple world map SVG */}
                  <svg viewBox="0 0 1000 500" className="w-full h-full opacity-30">
                    <path d="M150,100 Q200,80 250,100 T350,90 T450,100 T550,85 T650,100 T750,90 T850,100" fill="none" stroke="#8B7355" strokeWidth="2"/>
                    <ellipse cx="200" cy="200" rx="80" ry="100" fill="#D4C8B8"/>
                    <ellipse cx="500" cy="180" rx="150" ry="120" fill="#D4C8B8"/>
                    <ellipse cx="750" cy="220" rx="100" ry="80" fill="#D4C8B8"/>
                    <ellipse cx="850" cy="350" rx="60" ry="80" fill="#D4C8B8"/>
                  </svg>
                  {/* Location dots */}
                  {analytics?.locations?.slice(0, 20).map((loc, i) => {
                    if (!loc.lat || !loc.lon) return null;
                    const x = ((loc.lon + 180) / 360) * 100;
                    const y = ((90 - loc.lat) / 180) * 100;
                    return (
                      <div
                        key={i}
                        className="absolute w-2 h-2 bg-[#B8864B] rounded-full animate-pulse"
                        style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
                        title={`${loc.city}, ${loc.country}`}
                      />
                    );
                  })}
                  {(!analytics?.locations || analytics.locations.length === 0) && (
                    <div className="absolute inset-0 flex items-center justify-center text-[#8B7355] text-sm">
                      No location data yet
                    </div>
                  )}
                </div>
              </div>

              {/* Top Countries */}
              <div className="bg-white rounded-xl border border-[#E5DDD0] shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin size={18} className="text-[#5C4F42]" />
                  <h3 className="text-[#3D3229] font-semibold">Top Countries</h3>
                </div>
                {analytics?.countries?.length > 0 ? (
                  <div className="space-y-3">
                    {analytics.countries.slice(0, 5).map((country, i) => {
                      const maxCount = analytics.countries[0]?.count || 1;
                      const width = (country.count / maxCount) * 100;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-[#3D3229] text-sm w-24 truncate">{country.country}</span>
                          <div className="flex-1 h-2 bg-[#F5EBE0] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#B8864B] to-[#D4A574] rounded-full"
                              style={{ width: `${width}%` }}
                            />
                          </div>
                          <span className="text-[#8B7355] text-sm w-8 text-right">{country.count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-[#8B7355] text-sm">No country data yet</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default FounderDashboard;
